import { inject } from '@angular/core';
import { ActivatedRoute, CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, filter, first, map, withLatestFrom } from 'rxjs';
import { onUntouched } from '../../shared/loading-state/loadable';
import { getCompanies, getUserPermissions, setSelectedCompany } from '../store/auth.actions';
import { selectCompanies, selectSelectedCompany, selectUserProfile } from '../store/auth.selectors';
import { AuthService } from './auth.service';

/**
 * Account Guard collects and synchronizes Client (ei. Account / Company) parameter from URL string,
 * checks if the current user has access to the account, and checks if the account.
 *
 * If user has no access or an error occurred, user will be denied access to the page
 * and redirected to Account Selection screen.
 *
 * If user has access, but the account has no active subscription, user will be redirected to the
 * corresponding subscription selection screen.
 *
 * @author Anton Valeev
 */
export const hasAccountAccess: CanActivateFn = (routeSnapshot, state) => {
  const store = inject(Store);
  const auth = inject(AuthService);
  const router = inject(Router);
  const route = inject(ActivatedRoute);

  return combineLatest([store.select(selectCompanies), store.select(selectUserProfile)]).pipe(
    filter(([, user]) => !!user),
    onUntouched(() => store.dispatch(getCompanies()), 0),
    first(([c]) => !!c.value),
    withLatestFrom(store.select(selectSelectedCompany)),
    map(([[c], selected]) => {
      const companies = c.value!;

      // If there's a client parameter, check if it's valid and set it as selected account
      const clientParam = routeSnapshot.queryParamMap.get('client');
      const company =
        companies.find(c_ => c_.company_name === (clientParam ?? auth.selectedAccount)) ??
        companies[0];

      if (!company) return false;

      if (company.company_name !== selected?.company_name) {
        auth.selectedAccount = company.company_name;
        store.dispatch(setSelectedCompany({ company }));
        store.dispatch(getUserPermissions());
      }

      if (!clientParam) {
        void router.navigate([state.url.split('?')[0]], {
          relativeTo: route,
          queryParams: { client: company.company_name },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
        return false;
      }

      return true;
    })
  );
};
